import {
  CancellationToken,
  Connection,
  Disposable,
  InitializeParams,
  InlineCompletionContext,
  Position,
  TextDocumentIdentifier,
} from 'vscode-languageserver';
import { WorkflowAPI } from '@gitlab-lsp/workflow-api';
import { ConfigService, IClientConfig } from './config_service';
import { ClientFeatureFlags, FeatureFlagService } from './feature_flags';
import { log } from './log';
import { DuoProjectAccessCache } from './services/duo_access';
import { TRACKING_EVENTS, CODE_SUGGESTIONS_CATEGORY } from './tracking/constants';
import { canClientTrackEvent } from './tracking/utils';
import { TelemetryTracker, IClientContext } from './tracking/tracking_types';
import { VirtualFileSystemService } from './services/fs/virtual_file_system_service';

export type ChangeConfigOptions = { settings: IClientConfig };

export type CustomInitializeParams = InitializeParams & {
  initializationOptions?: IClientContext;
};

export const WORKFLOW_MESSAGE_NOTIFICATION = '$/gitlab/workflowMessage';

export interface MessageHandlerOptions {
  telemetryTracker: TelemetryTracker;
  configService: ConfigService;
  connection: Connection;
  featureFlagService: FeatureFlagService;
  duoProjectAccessCache: DuoProjectAccessCache;
  workflowAPI: WorkflowAPI | undefined;
  virtualFileSystemService: VirtualFileSystemService;
}

export interface ITelemetryNotificationParams {
  category: 'code_suggestions';
  action: TRACKING_EVENTS;
  context: {
    trackingId: string;
    optionId?: number;
  };
}

export interface IStartWorkflowParams {
  goal: string;
  image: string;
}

export const waitMs = (msToWait: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, msToWait);
  });

/* CompletionRequest represents LS client's request for either completion or inlineCompletion */
export interface CompletionRequest {
  textDocument: TextDocumentIdentifier;
  position: Position;
  token: CancellationToken;
  inlineCompletionContext?: InlineCompletionContext;
}

export class MessageHandler {
  #configService: ConfigService;

  #tracker: TelemetryTracker;

  #connection: Connection;

  #subscriptions: Disposable[] = [];

  #duoProjectAccessCache: DuoProjectAccessCache;

  #featureFlagService: FeatureFlagService;

  #workflowAPI: WorkflowAPI | undefined;

  #virtualFileSystemService: VirtualFileSystemService;

  constructor({
    telemetryTracker,
    configService,
    connection,
    featureFlagService,
    duoProjectAccessCache,
    workflowAPI = undefined,
    virtualFileSystemService,
  }: MessageHandlerOptions) {
    this.#configService = configService;
    this.#tracker = telemetryTracker;
    this.#connection = connection;
    this.#featureFlagService = featureFlagService;
    this.#workflowAPI = workflowAPI;

    this.#duoProjectAccessCache = duoProjectAccessCache;
    this.#virtualFileSystemService = virtualFileSystemService;
  }

  didChangeConfigurationHandler = async (
    { settings }: ChangeConfigOptions = { settings: {} },
  ): Promise<void> => {
    this.#configService.merge({
      client: settings,
    });

    // update Duo project access cache
    await this.#duoProjectAccessCache.updateCache({
      baseUrl: this.#configService.get('client.baseUrl') ?? '',
      workspaceFolders: this.#configService.get('client.workspaceFolders') ?? [],
    });

    const promises = (this.#configService.get('client')?.workspaceFolders ?? []).map(
      async (folder) => {
        try {
          return await this.#virtualFileSystemService.emitFilesForWorkspace(folder);
        } catch (e) {
          // FIXME: After `virtualFileSystemService` has been working for some time, we can change this to an error
          log.debug('Error in emitting files for workspace', e);
        }
        return null;
      },
    );

    await Promise.all(promises);
  };

  telemetryNotificationHandler = async ({
    category,
    action,
    context,
  }: ITelemetryNotificationParams) => {
    if (category === CODE_SUGGESTIONS_CATEGORY) {
      const { trackingId, optionId } = context;

      if (
        trackingId &&
        canClientTrackEvent(this.#configService.get('client.telemetry.actions'), action)
      ) {
        switch (action) {
          case TRACKING_EVENTS.ACCEPTED:
            if (optionId) {
              this.#tracker.updateCodeSuggestionsContext(trackingId, { acceptedOption: optionId });
            }
            this.#tracker.updateSuggestionState(trackingId, TRACKING_EVENTS.ACCEPTED);
            break;
          case TRACKING_EVENTS.REJECTED:
            this.#tracker.updateSuggestionState(trackingId, TRACKING_EVENTS.REJECTED);
            break;
          case TRACKING_EVENTS.CANCELLED:
            this.#tracker.updateSuggestionState(trackingId, TRACKING_EVENTS.CANCELLED);
            break;
          case TRACKING_EVENTS.SHOWN:
            this.#tracker.updateSuggestionState(trackingId, TRACKING_EVENTS.SHOWN);
            break;
          case TRACKING_EVENTS.NOT_PROVIDED:
            this.#tracker.updateSuggestionState(trackingId, TRACKING_EVENTS.NOT_PROVIDED);
            break;
          default:
            break;
        }
      }
    }
  };

  onShutdownHandler = () => {
    this.#subscriptions.forEach((subscription) => subscription?.dispose());
  };

  async #sendWorkflowErrorNotification(message: string) {
    await this.#connection.sendNotification(WORKFLOW_MESSAGE_NOTIFICATION, {
      message,
      type: 'error',
    });
  }

  startWorkflowNotificationHandler = async ({ goal, image }: IStartWorkflowParams) => {
    const duoWorkflow = this.#featureFlagService.isClientFlagEnabled(
      ClientFeatureFlags.DuoWorkflow,
    );
    if (!duoWorkflow) {
      await this.#sendWorkflowErrorNotification(`The Duo Workflow feature is not enabled`);
      return;
    }

    if (!this.#workflowAPI) {
      await this.#sendWorkflowErrorNotification('Workflow API is not configured for the LSP');
      return;
    }

    try {
      await this.#workflowAPI?.runWorkflow(goal, image);
    } catch (e) {
      log.error('Error in running workflow', e);
      await this.#sendWorkflowErrorNotification(`Error occurred while running workflow ${e}`);
    }
  };
}
